import React, { useMemo, useState } from "react";
import "./pricingCTA.css";
import { useNavigate } from "react-router-dom";

import StreamOutlinedIcon from "@mui/icons-material/StreamOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import BoltIcon from "@mui/icons-material/Bolt";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export default function PricingCTA() {
  const redirect = useNavigate();

  const [billing, setBilling] = useState("monthly");
  const [activePlan, setActivePlan] = useState("premium");
  const [activeSection, setActiveSection] = useState("Business Capacity");

  const plans = [
    {
      id: "standard",
      name: "Standard",
      price: 4850,
      description: "For small businesses getting started.",
      accent: "standard",
    },
    {
      id: "professional",
      name: "Professional",
      price: 7500,
      description: "For growing teams and operations.",
      accent: "professional",
    },
    {
      popular: true,
      id: "premium",
      name: "Premium",
      price: 16500,
      description: "For established businesses with complex operations.",
      accent: "premium",
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: 31850,
      description: "For advanced multi-location organizations.",
      accent: "enterprise",
    },
  ];

  const comparisonSections = [
    {
      heading: "Business Capacity",
      rows: [
        {
          title: "Unique Products",
          values: ["100", "500", "2,000", "15,000"],
        },
        {
          title: "Users",
          values: ["3", "6", "12", "45+"],
        },
        {
          title: "Business Locations",
          values: ["1", "2", "10", "Unlimited"],
        },
        {
          title: "Warehouses",
          values: ["1", "2", "10", "Unlimited"],
        },
        {
          title: "POS Registers",
          values: ["1", "4", "12", "Unlimited"],
        },
      ],
    },
    {
      heading: "Sales & Inventory",
      rows: [
        {
          title: "Point of Sale (POS)",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Barcode Generation",
          values: ["40", "200", "500", "Unlimited"],
        },
        {
          title: "QR Code Generation",
          values: ["40", "200", "500", "Unlimited"],
        },
        {
          title: "Stock Adjustments",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Stock Transfers",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Purchase Management",
          values: ["✓", "✓", "✓", "✓"],
        },
      ],
    },
    {
      heading: "Customers & Suppliers",
      rows: [
        {
          title: "Customers",
          values: ["500", "5,000", "50,000", "Unlimited"],
        },
        {
          title: "Suppliers",
          values: ["50", "500", "5,000", "Unlimited"],
        },
        {
          title: "Customer Credit/Debit",
          values: ["", "✓", "✓", "✓"],
        },
        {
          title: "Supplier Management",
          values: ["", "✓", "✓", "✓"],
        },
      ],
    },
    {
      heading: "Business Management",
      rows: [
        {
          title: "Finance Module",
          values: ["", "", "✓", "✓"],
        },
        {
          title: "Staff Management",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Warehouse Management",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Multi-Branch Management",
          values: ["", "", "", "✓"],
        },
        {
          title: "Multi-Company Support",
          values: ["", "", "✓", "✓"],
        },
      ],
    },
    {
      heading: "Reports & Analytics",
      rows: [
        {
          title: "Sales Reports",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Inventory Reports",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Financial Reports",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Business Analytics",
          values: ["", "", "Basic", "Advanced"],
        },
        {
          title: "Audit Logs",
          values: ["", "", "", "✓"],
        },
      ],
    },
    {
      heading: "Security & Backup",
      rows: [
        {
          title: "Two-Factor Authentication",
          values: ["", "✓", "✓", "✓"],
        },
        {
          title: "Custom Roles & Permissions",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Automatic Backup",
          values: ["", "", "✓", "✓"],
        },
        {
          title: "Backup Retention",
          values: ["-", "7 Days", "30 Days", "365 Days"],
        },
        {
          title: "SSO & IP Restrictions",
          values: ["", "", "", "✓"],
        },
      ],
    },
    {
      heading: "Integrations & Support",
      rows: [
        {
          title: "QuickBooks Integration",
          values: ["", "", "✓", "✓"],
        },
        {
          title: "Third-Party Apps",
          values: ["", "", "✓", "✓"],
        },
        {
          title: "Developer API",
          values: ["", "", "", "100K Requests/Day"],
        },
        {
          title: "Webhooks",
          values: ["", "", "", "✓"],
        },
        {
          title: "Email Support",
          values: ["✓", "✓", "✓", "✓"],
        },
        {
          title: "Live Chat",
          values: ["", "✓", "✓", "✓"],
        },
        {
          title: "Phone Support",
          values: ["", "✓", "✓", "✓"],
        },
        {
          title: "Priority Support",
          values: ["", "", "✓", "✓"],
        },
        {
          title: "Dedicated Account Manager",
          values: ["", "", "", "✓"],
        },
      ],
    },
  ];

  const activeSectionData = useMemo(
    () =>
      comparisonSections.find(
        (section) => section.heading === activeSection,
      ),
    [activeSection],
  );

  const getPlanIndex = (planId) =>
    plans.findIndex((plan) => plan.id === planId);

  const getValue = (row, planId) => {
    return row.values[getPlanIndex(planId)];
  };

  const renderFeatureValue = (value) => {
    if (value === "✓") {
      return (
        <span className="pricingFeatureCheck">
          <DoneIcon />
        </span>
      );
    }

    if (value === "" || value === "-") {
      return (
        <span className="pricingFeatureUnavailable">
          <RemoveIcon />
        </span>
      );
    }

    return <span className="pricingFeatureValue">{value}</span>;
  };

  const handlePlanSelect = (plan) => {
    redirect(
      `/create_new_account?plan=${encodeURIComponent(
        plan.id,
      )}&serviceId=${encodeURIComponent("347987sdafsdfa8afa")}`,
    );
  };

  return (
    <section className="homeCTAComparePlans">
      <div className="pricingCTAContainer">

        {/* HEADER */}

        <header className="pricingCTAHeader">
          <div className="pricingCTAHeaderBadge">
            <span>
              <StreamOutlinedIcon />
            </span>

            <strong>UNIVERSE INVENTORY</strong>
          </div>

          <h2>
            Pricing designed around
            <span> your growth.</span>
          </h2>

          <p>
            Start with the essentials, scale into advanced operations,
            and unlock the infrastructure your business needs as it grows.
          </p>

          <div className="pricingBillingSwitch">
            <button
              className={billing === "monthly" ? "active" : ""}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>

            <button
              className={billing === "yearly" ? "active" : ""}
              onClick={() => setBilling("yearly")}
            >
              Yearly
              <span>Save 20%</span>
            </button>
          </div>
        </header>

        {/* MOBILE PLAN SELECTOR */}

        <div className="pricingMobileSelector">
          <span>Compare plan</span>

          <div className="pricingMobileSelect">
            <select
              value={activePlan}
              onChange={(e) => setActivePlan(e.target.value)}
            >
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>

            <KeyboardArrowDownIcon />
          </div>
        </div>

        {/* CATEGORY NAVIGATION */}

        <div className="pricingCategoryNav">
          {comparisonSections.map((section, index) => (
            <button
              key={section.heading}
              className={
                activeSection === section.heading ? "active" : ""
              }
              onClick={() => setActiveSection(section.heading)}
            >
              <span>0{index + 1}</span>
              {section.heading}
            </button>
          ))}
        </div>

        {/* PLAN CARDS */}

        <div className="pricingPlans">
          <div className="pricingPlansIntro">
            <span>Choose your operating level</span>
            <p>
              Every plan is built around the same Universe Inventory
              ecosystem.
            </p>
          </div>

          {plans.map((plan) => (
            <article
              key={plan.id}
              className={`pricingPlanCard ${plan.accent} ${
                activePlan === plan.id ? "selected" : ""
              } ${plan.popular ? "popular" : ""}`}
              onMouseEnter={() => setActivePlan(plan.id)}
            >
              {plan.popular && (
                <div className="pricingPopularBadge">
                  <BoltIcon />
                  Most Popular
                </div>
              )}

              <div className="pricingPlanTop">
                <div className="pricingPlanIcon">
                  <BusinessCenterOutlinedIcon />
                </div>

                <span className="pricingPlanName">
                  {plan.name}
                </span>
              </div>

              <p className="pricingPlanDescription">
                {plan.description}
              </p>

              <div className="pricingPlanPrice">
                <small>₦</small>
                <strong>
                  {billing === "yearly"
                    ? Math.round(plan.price * 0.8).toLocaleString()
                    : plan.price.toLocaleString()}
                </strong>
                <span>/mo</span>
              </div>

              <button
                className="pricingPlanButton"
                onClick={() => handlePlanSelect(plan)}
              >
                Select {plan.name}
                <span>→</span>
              </button>
            </article>
          ))}
        </div>

        {/* ACTIVE CATEGORY FEATURE VIEW */}

        <div className="pricingFeaturePanel">
          <div className="pricingFeaturePanelHeader">
            <div>
              <span>FEATURE CATEGORY</span>
              <h3>{activeSectionData?.heading}</h3>
            </div>

            <p>
              Compare what each Universe Inventory plan provides.
            </p>
          </div>

          <div className="pricingFeatureRows">
            {activeSectionData?.rows.map((row) => (
              <div className="pricingFeatureRow" key={row.title}>
                <div className="pricingFeatureName">
                  <CheckCircleOutlineIcon />
                  <span>{row.title}</span>
                </div>

                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`pricingFeatureCell ${
                      activePlan === plan.id ? "active" : ""
                    }`}
                  >
                    {renderFeatureValue(
                      getValue(row, plan.id),
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* FULL COMPARISON */}

        <div className="pricingFullComparison">
          <div className="pricingComparisonHeading">
            <div>
              <span>FULL COMPARISON</span>
              <h3>Everything side by side.</h3>
            </div>

            <p>
              Explore every capability available across the plans.
            </p>
          </div>

          <div className="pricingComparisonScroller">
            <div className="pricingComparisonGrid">

              <div className="pricingComparisonFeatureHeader">
                <span>Features</span>
              </div>

              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`pricingComparisonPlanHeader ${
                    plan.popular ? "popular" : ""
                  }`}
                >
                  {plan.popular && (
                    <span className="pricingTablePopular">
                      MOST POPULAR
                    </span>
                  )}

                  <strong>{plan.name}</strong>

                  <span>
                    ₦{plan.price.toLocaleString()}
                    <small>/mo</small>
                  </span>

                  <button
                    onClick={() => handlePlanSelect(plan)}
                  >
                    Get Started
                  </button>
                </div>
              ))}

              {comparisonSections.map((section) => (
                <React.Fragment key={section.heading}>
                  <div className="pricingTableSectionTitle">
                    <StreamOutlinedIcon />
                    <span>{section.heading}</span>
                  </div>

                  <div className="pricingTableSectionFill" />
                  <div className="pricingTableSectionFill" />
                  <div className="pricingTableSectionFill" />

                  {section.rows.map((row) => (
                    <React.Fragment key={row.title}>
                      <div className="pricingTableFeatureName">
                        {row.title}
                      </div>

                      {plans.map((plan) => (
                        <div
                          key={`${row.title}-${plan.id}`}
                          className={`pricingTableValue ${
                            activePlan === plan.id
                              ? "active"
                              : ""
                          }`}
                        >
                          {renderFeatureValue(
                            getValue(row, plan.id),
                          )}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM CTA */}

        <div className="pricingBottomCTA">
          <div className="pricingBottomGlow" />

          <div className="pricingBottomContent">
            <span>READY WHEN YOU ARE</span>

            <h3>
              Build a smarter operation
              <br />
              with Universe Inventory.
            </h3>

            <p>
              Start small. Build momentum. Scale without rebuilding
              your business systems.
            </p>

            <button
              onClick={() =>
                redirect(
                  "/create_new_account?plan=professional&serviceId=347987sdafsdfa8afa",
                )
              }
            >
              Start Building
              <span>→</span>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}


// OLDER COMPONENT BELOW ((((((((((((((((()))))))))))))))))

// import React, { useState } from "react";
// import "./pricingCTA.css";
// import { useNavigate } from "react-router-dom";
// import StreamOutlinedIcon from "@mui/icons-material/StreamOutlined";
// import RemoveIcon from "@mui/icons-material/Remove";
// import CloseIcon from "@mui/icons-material/Close";
// import DoneIcon from "@mui/icons-material/Done";

// export default function PricingCTA() {
//   const redirect = useNavigate();
//    const comparisonSections = [
//     {
//       heading: "Business Capacity",
//       rows: [
//         {
//           title: "Unique Products",
//           values: ["100", "500", "2,000", "15,000"],
//         },
//         {
//           title: "Users",
//           values: [" 3", " 6", "12", "45+"],
//         },
//         {
//           title: "Business Locations",
//           values: [" 1", " 2", "10", "Unlimited"],
//         },
//         {
//           title: "Warehouses",
//           values: [" 1", " 2", "10", "Unlimited"],
//         },
//         {
//           title: "POS Registers",
//           values: [" 1", " 4", "12", "Unlimited"],
//         },
//         // {
//         //   title: "Cloud Storage",
//         //   values: ["1 GB", "5 GB", "20 GB", "100 GB+"],
//         // },
//       ],
//     },

//     {
//       heading: "Sales & Inventory",
//       rows: [
//         {
//           title: "Point of Sale (POS)",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Barcode Generation",
//           values: ["40", "200", "500", "Unlimited"],
//         },
//         {
//           title: "QR Code Generation",
//           values: ["40", "200", "500", "Unlimited"],
//         },
//         {
//           title: "Stock Adjustments",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Stock Transfers",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Purchase Management",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//       ],
//     },

//     {
//       heading: "Customers & Suppliers",
//       rows: [
//         {
//           title: "Customers",
//           values: ["500", "5,000", "50,000", "Unlimited"],
//         },
//         {
//           title: "Suppliers",
//           values: ["50", "500", "5,000", "Unlimited"],
//         },
//         {
//           title: "Customer Credit/Debit",
//           values: ["", "✓", "✓", "✓"],
//         },
//         {
//           title: "Supplier Management",
//           values: ["", "✓", "✓", "✓"],
//         },
//       ],
//     },

//     {
//       heading: "Business Management",
//       rows: [
//         {
//           title: "Finance Module",
//           values: ["", "", "✓", "✓"],
//         },
//         {
//           title: "Staff Management",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Warehouse Management",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Multi-Branch Management",
//           values: ["", "", "", "✓"],
//         },
//         {
//           title: "Multi-Company Support",
//           values: ["", "", "✓", "✓"],
//         },
//       ],
//     },

//     {
//       heading: "Reports & Analytics",
//       rows: [
//         {
//           title: "Sales Reports",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Inventory Reports",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Financial Reports",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Business Analytics",
//           values: ["", "", "Basic", "Advanced"],
//         },
//         {
//           title: "Audit Logs",
//           values: ["", "", "", "✓"],
//         },
//       ],
//     },

//     {
//       heading: "Security & Backup",
//       rows: [
//         {
//           title: "Two-Factor Authentication",
//           values: ["", "✓", "✓", "✓"],
//         },
//         {
//           title: "Custom Roles & Permissions",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Automatic Backup",
//           values: ["", "", "✓", "✓"],
//         },
//         {
//           title: "Backup Retention",
//           values: ["-", "7 Days", "30 Days", "365 Days"],
//         },
//         {
//           title: "SSO & IP Restrictions",
//           values: ["", "", "", "✓"],
//         },
//       ],
//     },

//     {
//       heading: "Integrations & Support",
//       rows: [
//         {
//           title: "QuickBooks Integration",
//           values: ["", "", "✓", "✓"],
//         },
//         {
//           title: "Third-Party Apps",
//           values: ["", "", "✓", "✓"],
//         },
//         {
//           title: "Developer API",
//           values: ["", "", "", "100K Requests/Day"],
//         },
//         {
//           title: "Webhooks",
//           values: ["", "", "", "✓"],
//         },
//         {
//           title: "Email Support",
//           values: ["✓", "✓", "✓", "✓"],
//         },
//         {
//           title: "Live Chat",
//           values: ["", "✓", "✓", "✓"],
//         },
//         {
//           title: "Phone Support",
//           values: ["", "✓", "✓", "✓"],
//         },
//         {
//           title: "Priority Support",
//           values: ["", "", "✓", "✓"],
//         },
//         {
//           title: "Dedicated Account Manager",
//           values: ["", "", "", "✓"],
//         },
//       ],
//     },
//   ];
//   return (
//     <div className="homeCTAComparePlans fx-cl space6">
//       <div className="compareHomeCTA-title fx-cl spacem">
//         <h2 className="compareHomeCTA-title-btn">
//           Find the Right Plan for Your Business
//         </h2>
//         <p className="fs7">Choose the Plan That Fits Your Growth</p>
//       </div>
//       <div className="fx-cl space3">
//         <div className="advanceTableHomeComparePlanCTA fs4 fw300">
//           <div className="advanceTableTbody advanceTableTbodyFirst fx-cl">
//             <div className="tableHeaderPrices fs3">
//               <span style={{ fontstyle: "italic" }}>&nbsp;</span>
//             </div>
//             {comparisonSections.map((section, sectionIndex) => (
//               <figure className="fx-cl" key={sectionIndex}>
//                 {/* Heading Row */}
//                 <span className="subHeadingAdvTb fw600">{section.heading}</span>

//                 {/* Feature Rows */}
//                 {section.rows.map((row, rowIndex) => (
//                   <div key={rowIndex} className="cellDataHmPricing">
//                     <span>{row.title}</span>
//                   </div>
//                 ))}
//               </figure>
//             ))}
//           </div>
//           <div className="advanceTableTbody adtbEnterprise fx-cl">
//             {" "}
//             <div className="tableHeaderPrices">
//               <div className="tableHeaderPricesCard fx-cl fx-ac spacem">
//                 <span className="fw500">
//                   <strong>Enterprise</strong>
//                 </span>
//                 <span>₦31,850/mo</span>
//                 <button
//                   className="planExecuteBtnHomeCTA"
//                   onClick={() =>
//                     redirect(
//                       `/create_new_account?plan=${encodeURIComponent("enterprise")}&serviceId=${encodeURIComponent("347987sdafsdfa8afa")}`,
//                     )
//                   }
//                 >
//                   Select Plan
//                 </button>
//               </div>
//             </div>
//             {comparisonSections.map((section, sectionIndex) => (
//               <figure className="fx-cl" key={3}>
//                 {/* Heading Row */}
//                 <span className="subHeadingAdvTb fw500">&nbsp;</span>

//                 {/* Feature Rows */}
//                 {section.rows?.map((val, i) => (
//                   <div key={i} className="cellDataHmPricing">
//                     {val.values[3] === "✓" && (
//                       <DoneIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "oklch(0.64 0.11 162.91)",
//                         }}
//                       />
//                     )}
//                     {val.values[3].length >= 2 && val.values[3]}
//                     {val.values[3] === "" && (
//                       <CloseIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "red",
//                         }}
//                       />
//                     )}
//                   </div>
//                 ))}
//               </figure>
//             ))}
//           </div>
//           <div className="advanceTableTbody fx-cl">
//             {" "}
//             <div className="tableHeaderPrices">
//               <div className="tableHeaderPricesCard fx-cl fx-ac spacem">
//                 <span className="fw500">
//                   <strong>Premium</strong>
//                 </span>
//                 <span>₦16,500/mo</span>
//                 <button
//                   className="planExecuteBtnHomeCTA"
//                   onClick={() =>
//                     redirect(
//                       `/create_new_account?plan=${encodeURIComponent("premium")}&serviceId=${encodeURIComponent("347987sdafsdfa8afa")}`,
//                     )
//                   }
//                 >
//                   Select Plan
//                 </button>
//               </div>
//             </div>
//             {comparisonSections.map((section, sectionIndex) => (
//               <figure className="fx-cl" key={2}>
//                 {/* Heading Row */}
//                 <span className="subHeadingAdvTb fw500">&nbsp;</span>

//                 {/* Feature Rows */}
//                 {section.rows?.map((val, i) => (
//                   <div key={i} className="cellDataHmPricing">
//                     {val.values[2] === "✓" && (
//                       <DoneIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "oklch(0.64 0.11 162.91)",
//                         }}
//                       />
//                     )}
//                     {val.values[2].length >= 2 && val.values[2]}
//                     {val.values[2] === "" && (
//                       <CloseIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "red",
//                         }}
//                       />
//                     )}
//                   </div>
//                 ))}
//               </figure>
//             ))}
//           </div>
//           <div className="advanceTableTbody fx-cl">
//             {" "}
//             <div className="tableHeaderPrices">
//               <div className="tableHeaderPricesCard fx-cl fx-ac spacem">
//                 <span className="fw500">
//                   <strong>Professional</strong>
//                 </span>
//                 <span>₦7,500/mo</span>
//                 <button
//                   className="planExecuteBtnHomeCTA"
//                   onClick={() =>
//                     redirect(
//                       `/create_new_account?plan=${encodeURIComponent("professional")}&serviceId=${encodeURIComponent("347987sdafsdfa8afa")}`,
//                     )
//                   }
//                 >
//                   Select Plan
//                 </button>
//               </div>
//             </div>
//             {comparisonSections.map((section, sectionIndex) => (
//               <figure className="fx-cl" key={1}>
//                 {/* Heading Row */}
//                 <span className="subHeadingAdvTb fw500">&nbsp;</span>

//                 {/* Feature Rows */}
//                 {section.rows?.map((val, i) => (
//                   <div key={i} className="cellDataHmPricing">
//                     {val.values[1] === "✓" && (
//                       <DoneIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "oklch(0.64 0.11 162.91)",
//                         }}
//                       />
//                     )}
//                     {val.values[1].length >= 2 && val.values[1]}
//                     {val.values[1] === "" && (
//                       <CloseIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "red",
//                         }}
//                       />
//                     )}
//                   </div>
//                 ))}
//               </figure>
//             ))}
//           </div>
//           <div className="advanceTableTbody fx-cl">
//             {" "}
//             <div className="tableHeaderPrices">
//               <div className="tableHeaderPricesCard fx-cl fx-ac spacem">
//                 <span className="fw500">
//                   <strong>Standard</strong>
//                 </span>
//                 <span>₦4,850/mo</span>
//                 <button
//                   className="planExecuteBtnHomeCTA"
//                   onClick={() =>
//                     redirect(
//                       `/create_new_account?plan=${encodeURIComponent("standard")}&serviceId=${encodeURIComponent("347987sdafsdfa8afa")}`,
//                     )
//                   }
//                 >
//                   Select Plan
//                 </button>
//               </div>
//             </div>
//             {comparisonSections.map((section, sectionIndex) => (
//               <figure className="fx-cl" key={0}>
//                 {/* Heading Row */}
//                 <span className="subHeadingAdvTb fw500">&nbsp;</span>

//                 {/* Feature Rows */}
//                 {section.rows?.map((val, i) => (
//                   <div key={i} className="cellDataHmPricing">
//                     {val.values[0] === "✓" && (
//                       <DoneIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "oklch(0.64 0.11 162.91)",
//                         }}
//                       />
//                     )}
//                     {val.values[0].length >= 2 && val.values[0]}
//                     {val.values[0] === "" && (
//                       <CloseIcon
//                         style={{
//                           fontSize: "2.8rem",
//                           color: "red",
//                         }}
//                       />
//                     )}
//                   </div>
//                 ))}
//               </figure>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }
